import React from 'react';
import { Link } from 'react-router-dom';
import {
  TextInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Checkbox,
  Tooltip,
} from '@c2fo/liquidity';
import type { DivisionWithPotentialBid } from '../utils/templateRenderer';

const ROW_CHECK_LOGIC_TOOLTIP =
  'Rows can be selected only when the market is enabled and the division has eligible invoice amount (DPE) greater than zero. Disabled rows do not meet these conditions.';

interface OfferSimulatorTableProps {
  filteredDivisions: DivisionWithPotentialBid[];
  allDivisionsEnabled: boolean;
  simulateOffersLoading: boolean;
  handleToggleAll: () => void;
  handleDivisionEnabledChange: (index: number, enabled: boolean) => void;
  handleDivisionBidChange: (
    divisionKey: string,
    field: 'maxBid' | 'isDiscountBidding',
    value: number | boolean | null,
  ) => void;
  totals: {
    blendedRate: string;
    discount: string;
    discountPercent: string;
    discountedAmount: string;
  };
}

export const OfferSimulatorTable: React.FC<OfferSimulatorTableProps> = ({
  filteredDivisions,
  allDivisionsEnabled,
  simulateOffersLoading,
  handleToggleAll,
  handleDivisionEnabledChange,
  handleDivisionBidChange,
  totals,
}) => {
  return (
    <div className="overflow-x-auto mb-6">
      <table className="w-full border-collapse text-xs bg-white border border-gray-300">
        <thead>
          <tr>
            <th colSpan={3} className="p-3 text-left border border-gray-300 bg-gray-50">
              <div className="flex items-center gap-2">
                <Checkbox checked={allDivisionsEnabled} onChange={handleToggleAll} id="selectAll" />
                <Tooltip.Provider delayDuration={300}>
                  <Tooltip>
                    <Tooltip.Trigger asChild>
                      <Label htmlFor="selectAll" className="font-normal cursor-pointer">
                        Toggle All
                      </Label>
                    </Tooltip.Trigger>
                    <Tooltip.Content side="bottom" variant="dark" className="max-w-xs">
                      {ROW_CHECK_LOGIC_TOOLTIP}
                    </Tooltip.Content>
                  </Tooltip>
                </Tooltip.Provider>
              </div>
            </th>
            <th colSpan={3} className="p-3 text-center border border-gray-300 bg-gray-50">
              Invoices
            </th>
            <th colSpan={3} className="p-3 text-center border border-gray-300 bg-gray-50">
              Current Bid
            </th>
            <th colSpan={2} className="p-3 text-center border border-gray-300 bg-gray-50">
              Potential Bid
            </th>
            <th colSpan={4} className="p-3 text-center border border-gray-300 bg-gray-50">
              Simulated Results
            </th>
          </tr>
          <tr>
            <th className="p-3 text-left border border-gray-300 bg-gray-50"></th>
            <th className="p-3 text-left border border-gray-300 bg-gray-50">Division</th>
            <th className="p-3 text-left border border-gray-300 bg-gray-50">Market</th>
            <th className="p-3 text-right border border-gray-300 bg-gray-50">Clearing</th>
            <th className="p-3 text-right border border-gray-300 bg-gray-50">Eligible</th>
            <th className="p-3 text-right border border-gray-300 bg-gray-50">DPE</th>
            <th className="p-3 text-center border border-gray-300 bg-gray-50">Status</th>
            <th className="p-3 text-center border border-gray-300 bg-gray-50">Type</th>
            <th className="p-3 text-center border border-gray-300 bg-gray-50">Rate</th>
            <th className="p-3 text-center border border-gray-300 bg-gray-50">Potential Bid</th>
            <th className="p-3 text-center border border-gray-300 bg-gray-50">Offer Type</th>
            <th className="p-3 text-right border border-gray-300 bg-gray-50">Rate</th>
            <th className="p-3 text-right border border-gray-300 bg-gray-50">Discount</th>
            <th className="p-3 text-right border border-gray-300 bg-gray-50">%</th>
            <th className="p-3 text-right border border-gray-300 bg-gray-50">Net</th>
          </tr>
        </thead>
        <tbody>
          {filteredDivisions.map((division, index) => {
            const divisionKey = `${division.marketUuid}-${division.takerUuid}`;
            const canEnable = division.marketIsEnabled && division.eligibleInvoiceAmount > 0;
            return (
              <tr key={divisionKey}>
                <td className="p-3 border border-gray-300">
                  <Checkbox
                    checked={division.potentialBid.enabled}
                    onChange={(e) => handleDivisionEnabledChange(index, e.target.checked)}
                    id={`sim-${index}`}
                    disabled={!canEnable}
                  />
                </td>
                <td className="p-3 border border-gray-300">
                  {division.makerOrganizationUuid && (division.takerUuid || division.id) ? (
                    <Link
                      to={`/divisions/${division.makerOrganizationUuid}/${division.takerUuid || division.id}`}
                      className="text-blue-600 no-underline hover:underline"
                    >
                      {division.normalizedName}
                    </Link>
                  ) : (
                    <Label htmlFor={`sim-${index}`} className="font-normal cursor-pointer">
                      {division.normalizedName}
                    </Label>
                  )}
                </td>
                <td className="p-3 border border-gray-300">
                  {division.marketUuid ? (
                    <Link to={`/markets/${division.marketUuid}`} className="text-blue-600 no-underline hover:underline">
                      {division.makerOrganizationName} ({division.currency})
                    </Link>
                  ) : (
                    <span>
                      {division.makerOrganizationName} ({division.currency})
                    </span>
                  )}
                </td>
                <td className="p-3 text-right border border-gray-300">
                  {division.acceptedInvoiceAmount > 0 ? `$${division.acceptedInvoiceAmount.toFixed(2)}` : '-'}
                </td>
                <td className="p-3 text-right border border-gray-300">${division.eligibleInvoiceAmount.toFixed(2)}</td>
                <td className="p-3 text-right border border-gray-300">{division.eligibleDpeWeightedAvg}</td>
                <td className="p-3 text-center border border-gray-300">
                  {division.takerConfigId && (
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        division.takerConfigIsEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {division.takerConfigIsEnabled ? 'OFFER ON' : 'OFFER OFF'}
                    </span>
                  )}
                </td>
                <td className="p-3 text-center border border-gray-300">
                  {division.takerConfigId && (
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        division.takerConfigIsDiscountBidding
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {division.takerConfigIsDiscountBidding ? 'DISC' : 'APR'}
                    </span>
                  )}
                </td>
                <td className="p-3 text-center border border-gray-300">
                  {division.takerConfigId ? `${division.normalizedBid}%` : '-'}
                </td>
                <td className="p-3 border border-gray-300">
                  {division.marketIsEnabled ? (
                    <TextInput
                      type="number"
                      step="any"
                      min="0"
                      max="100"
                      value={division.potentialBid.maxBid ?? ''}
                      onChange={(e) =>
                        handleDivisionBidChange(divisionKey, 'maxBid', parseFloat(e.target.value) || null)
                      }
                      className="w-20"
                      required={division.potentialBid.enabled}
                    />
                  ) : (
                    <span className="text-gray-500 text-xs">disabled market</span>
                  )}
                </td>
                <td className="p-3 border border-gray-300">
                  {division.marketIsEnabled ? (
                    <Select
                      value={division.potentialBid.isDiscountBidding ? 'true' : 'false'}
                      onValueChange={(value) =>
                        handleDivisionBidChange(divisionKey, 'isDiscountBidding', value === 'true')
                      }
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="false">APR</SelectItem>
                        <SelectItem value="true">Discount</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-gray-500 text-xs">disabled market</span>
                  )}
                </td>
                <td className="p-3 text-right border border-gray-300">
                  {simulateOffersLoading ? (
                    <span>♾</span>
                  ) : division.simulation ? (
                    `${division.simulation.maxBid.toFixed(2)}%`
                  ) : (
                    ''
                  )}
                </td>
                <td className="p-3 text-right border border-gray-300">
                  {simulateOffersLoading ? (
                    <span>♾</span>
                  ) : division.simulation ? (
                    `$${division.simulation.discount.toFixed(2)}`
                  ) : (
                    ''
                  )}
                </td>
                <td className="p-3 text-right border border-gray-300">
                  {simulateOffersLoading ? (
                    <span>♾</span>
                  ) : division.simulation ? (
                    `${(division.simulation.discountPercent * 100).toFixed(2)}%`
                  ) : (
                    ''
                  )}
                </td>
                <td className="p-3 text-right border border-gray-300">
                  {simulateOffersLoading ? (
                    <span>♾</span>
                  ) : division.simulation ? (
                    `$${division.simulation.discountedAmount.toFixed(2)}`
                  ) : (
                    ''
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={10} className="p-3 border border-gray-300"></td>
            <td className="p-3 font-semibold border border-gray-300 bg-gray-50">Totals</td>
            <td className="p-3 text-center border border-gray-300 bg-gray-50" title="Blended Rate">
              {totals.blendedRate}
            </td>
            <td className="p-3 text-right border border-gray-300 bg-gray-50">{totals.discount}</td>
            <td className="p-3 text-right border border-gray-300 bg-gray-50">{totals.discountPercent}</td>
            <td className="p-3 text-right border border-gray-300 bg-gray-50">{totals.discountedAmount}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
